// libraries
import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {buildImageChangerConfig, hasValidCrop} from '~/utils/icon';
// actions
import {useDispatch} from 'react-redux';
import {crop as setCrop} from '~/actions/IconActions';
// constants
import IconConstants from '~/constants/DesignIconConstants';
import {MAXIMUM_DIMENSION} from '~/constants/UploadConstants';
import RemoteServices from '~/constants/RemoteServices';
import {iconPropType} from '~/data/records/icons';

import './index.scss';
import {pushGtmEvent} from '~/utils/analytics';

export const generateImageUrl = icon => {
  if (!icon?.url) return null;
  const url = RemoteServices.upload + icon.url;
  return `${url}?max_dimension=${MAXIMUM_DIMENSION}&client-name=ndx&client-version=${import.meta.env.NDX_REVISION}`;
};

export const getIconManipulateOptions = (icon, scale) => {
  return buildImageChangerConfig(icon.toJS(), scale);
};

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
const UploadImage = ({handleError, onRender, options, scale}) => {
  const icon = options.toJS?.() || options;
  const dispatch = useDispatch();
  const [imageChanger, setImageChanger] = useState();
  const canvasContainerRef = useRef();
  const setImage = useCallback(
    async config => {
      if (!canvasContainerRef.current || !imageChanger) {
        return;
      }

      await imageChanger
        .setFilters(config)
        .getCanvas()
        .then(data => {
          const {img: canvas, crop} = data;

          // Use automatic cropping data from image changer.
          // Currently, only checks for transparent backgrounds to remove.
          if (!icon.crop && hasValidCrop(crop, true)) {
            dispatch(setCrop(icon, crop, true));
            pushGtmEvent({
              event: 'lab_edit_tools',
              tool_icon_type: 'upload',
              tool_name: 'auto_crop'
            });
          }

          canvas.className = 'ndx-UploadImage';
          canvas.style.height = '100%';
          canvas.style.width = '100%';

          // This assumes that the structure of this component is the canvasContainer ref,
          // with a single canvas element as its child.
          // This is done since ImageChanger gives back a raw canvas html element
          const currentContent = canvasContainerRef.current?.childNodes[0];
          if (currentContent) {
            canvasContainerRef.current.replaceChild(canvas, currentContent);
            onRender({
              naturalWidth: canvas.width,
              naturalHeight: canvas.height
            });
          }
        })
        .catch(err => {
          handleError(err);
        });
    },
    [canvasContainerRef, handleError, onRender, icon, imageChanger, dispatch]
  );

  useEffect(() => {
    import('image_changer').then(ic => {
      setImageChanger(
        new ic.ImageChanger(
          IconConstants.TYPES.upload,
          generateImageUrl(options)
        )
      );
    });
  }, []);

  useEffect(() => {
    const iconManipulateOptions = buildImageChangerConfig(icon, scale);
    setImage(iconManipulateOptions);
  }, [icon, scale, setImage, imageChanger]);

  return (
    <div className="ndx-UploadImage-container" ref={canvasContainerRef}>
      <canvas />
    </div>
  );
};

UploadImage.displayName = 'UploadImage';

const propTypes = {
  handleError: PropTypes.func,
  options: iconPropType,
  scale: PropTypes.number,
  onRender: PropTypes.func
};

UploadImage.propTypes = propTypes;

UploadImage.defaultProps = {
  handleError: function () {},
  rotation: 0,
  scale: 20,
  onRender: function () {}
};

export default UploadImage;
